import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import useFormSubmit from "../../../../components/Form/useFormSubmit";
import { useDateFormat } from "../../../../utils/hooks/useDateFormat";
import { useAxiosRequest } from "../../../../utils/hooks/useAxios";
import { openNotification } from "../../../../components/Notification";
import { PAGE_IOR_MY_REPORTS, PAGE_IOR_REPORTS } from "../../routes";
import { prepareFormData, showCollapsedBoxErrors } from "./methods";

/**
 * Encapsulates all state, effects, and event handlers for the IOR ReportForm component.
 *
 * @param {object} params
 * @param {object} params.data - Existing report data (empty object for new reports).
 * @param {object} params.form - Ant Design Form instance.
 * @param {*} params.formWatch - Watched value of the riskAnalysis.riskLevel field.
 * @param {Function} params.formatLocaleUTC - UTC-aware date formatter.
 * @param {Function} params.format - Date formatter.
 * @returns {object} State values, setters, handlers, derived values, and request state.
 */
const useReportFormState = ({ data, form, formWatch, formatLocaleUTC, format }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { formatLocale } = useDateFormat();

  const [success, setSuccess] = useState(false);
  const [collapseErrors, setCollapseErrors] = useState({
    basic_data: false,
    investigation: false,
    first_risk_analysis: false,
    second_risk_analysis: false,
    management_summary: false
  });
  const [hasSecondRiskAnalysis, setHasSecondRiskAnalysis] = useState(
    data?.riskAnalysis?.riskLevel?.corrective_action_is_required
  );
  const [isRootCause, setIsRootCause] = useState(!!data.rootCause?.length);
  const [isRootCauseCollapsed, setIsRootCauseCollapsed] = useState(false);
  const [basicDataSaved, setBasicDataSaved] = useState(data);
  const [rootCauses, setRootCauses] = useState(null);
  const [hasRootCauseError, setHasRootCauseError] = useState(false);

  const { loading, submit, generalError } = useFormSubmit({
    route: basicDataSaved?.basic_data?.id ? `ior/report/${basicDataSaved?.basic_data?.id}` : "ior/report",
    method: basicDataSaved?.basic_data?.id ? "put" : "post",
    form,
    transformer: (values) => {
      return prepareFormData(values, formatLocaleUTC, format, rootCauses);
    },
    onSuccess: (response) => {
      openNotification({
        message: t("ior.new_report.notifications.report_data_saved"),
        type: "success",
        placement: "bottom"
      });
      if (setBasicDataSaved) {
        setBasicDataSaved(response.data);
      }
    },
    onFailed: (err, generalErrors, formErrors) => {
      openNotification({
        message: t("ior.new_report.notifications.report_data_not_saved"),
        type: "error",
        placement: "bottom"
      });
      showCollapsedBoxErrors(form, formErrors, collapseErrors, setCollapseErrors);
    }
  });

  // Keep hasSecondRiskAnalysis in sync whenever the user changes the risk level selection
  useEffect(() => {
    if (formWatch) {
      setHasSecondRiskAnalysis(formWatch.corrective_action_is_required);
    }
  }, [formWatch]);

  // Fetch the active risk matrix image
  const riskMatrix = useAxiosRequest({
    route: `ior/risk-matrices/active-matrix?${
      data?.basic_data?.id && `report_id=${data.basic_data.id}`
    }&with%5B%5D=image`
  });

  /**
   * Navigates back to the correct report list based on how the user arrived at this page.
   */
  const onClose = () => {
    history.push({
      pathname: history?.location?.state?.page === "REPORTS" ? PAGE_IOR_REPORTS : PAGE_IOR_MY_REPORTS
    });
  };

  /** Receives updated root-cause data from the ReportRootCause section component. */
  const updateRootCauses = (res) => setRootCauses(res);

  /**
   * Guards the full form submission against missing root causes.
   * For an existing report, at least one root cause must be assigned before saving.
   */
  const submitForm = () => {
    if (isRootCause || !data?.basic_data?.id) {
      setHasRootCauseError(false);
      submit();
    } else {
      setIsRootCauseCollapsed(true);
      setHasRootCauseError(true);
      const errorFieldElement = document.getElementsByClassName("report-root-cause")[0];
      errorFieldElement && window.scrollTo({ top: errorFieldElement.offsetTop - 80, behavior: "smooth" });
    }
  };

  return {
    success,
    setSuccess,
    collapseErrors,
    setCollapseErrors,
    hasSecondRiskAnalysis,
    isRootCause,
    setIsRootCause,
    isRootCauseCollapsed,
    basicDataSaved,
    setBasicDataSaved,
    hasRootCauseError,
    loading,
    generalError,
    riskMatrix,
    formatLocale,
    onClose,
    updateRootCauses,
    submitForm
  };
};

export default useReportFormState;
